import styled from '@emotion/styled';

type VariantProps = { $variant?: 'warning' | 'success' };

/* Content area inside the scroll body */
export const WarningModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.lg};
`;

/* A section with icon + content side by side */
export const WarningModalSection = styled.div`
  display: flex;
  gap: ${p => p.theme.s.xs};
  align-items: flex-start;

  & > svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

/* Text paragraph inside a section */
export const WarningModalSectionText = styled.p`
  margin: 0;
  font-size: ${p => p.theme.typography?.bodyMd?.fontSize ?? '1rem'};
  line-height: ${p => p.theme.typography?.bodyMd?.lineHeight ?? '1.5rem'};
  color: ${p => p.theme.palette.text.secondary};

  & strong {
    font-weight: 600;
  }
`;

/* Divider row: label --- line --- chevron */
export const WarningModalDividerRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xs};
  margin-top: ${p => p.theme.s.md};
`;

export const WarningModalDividerLabel = styled.span<VariantProps>`
  font-size: ${p => p.theme.typography?.bodyXs?.fontSize ?? '0.75rem'};
  font-weight: 600;
  line-height: ${p => p.theme.typography?.bodyXs?.lineHeight ?? '1rem'};
  color: ${p =>
    p.$variant === 'success'
      ? p.theme.palette.fg.successPrimary
      : p.theme.palette.fg.warningPrimary};
  flex-shrink: 0;
  white-space: nowrap;
`;

export const WarningModalDividerLine = styled.div<VariantProps>`
  flex: 1;
  min-width: 0;
  border-top: 1px solid
    ${p =>
      p.$variant === 'success'
        ? p.theme.palette.fg.successPrimary
        : p.theme.palette.fg.warningPrimary};
`;

/* Chevron toggle button on divider row */
export const WarningModalChevronButton = styled.button<VariantProps>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${p =>
    p.$variant === 'success'
      ? p.theme.palette.fg.successPrimary
      : p.theme.palette.fg.warningPrimary};

  & svg {
    color: inherit;
  }
`;

/* List of items below a divider */
export const WarningModalItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.s.xs};
  margin-top: ${p => p.theme.s.md};
`;

/* Yellow card for excluded items */
export const WarningModalItemCard = styled.div<{ $variant?: string }>`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xxs};
  padding: ${p => p.theme.s.md};
  border-radius: ${p => p.theme.s.xs};
  background-color: ${p => p.theme.palette.bg.warningSubtle};
  font-size: ${p => p.theme.typography?.bodySm?.fontSize ?? '0.875rem'};
  line-height: ${p => p.theme.typography?.bodySm?.lineHeight ?? '1.25rem'};
  color: ${p => p.theme.palette.text.secondary};
`;

/* Plain text row for editable items (no card background) */
export const WarningModalItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xxs};
  font-size: ${p => p.theme.typography?.bodySm?.fontSize ?? '0.875rem'};
  line-height: ${p => p.theme.typography?.bodySm?.lineHeight ?? '1.25rem'};
  color: ${p => p.theme.palette.text.secondary};
`;

export const WarningModalItemName = styled.span`
  font-weight: 600;
  color: ${p => p.theme.palette.text.primary};
`;

export const WarningModalItemId = styled.span`
  color: ${p => p.theme.palette.text.secondary};
`;
